import React, { useCallback } from 'react';
import { ContentPositioner } from '../SideBySide/ContentPositioner';
import Image from '@jetshop/ui/Image/Image';
import { Above } from '@jetshop/ui/Breakpoints';
import { Link } from 'react-router-dom';
import { cx } from 'linaria';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import { FakeCTA } from './FakeCTA';
import { useTracking } from '../../../../utils/tracking/useTracking';
import { useInViewTracking } from '../../../../utils/tracking/useInViewTracking';

const HtmlBannerRowItemWrapper = styled(Link)`
  position: relative;
  text-decoration: none;
  display: block;
  z-index: 1;

  --thirtyPercentBlack: #0000004d;
  --fiftyPercentBlack: #00000080;
  --gradientColor: ${props => props.gradientcolor};

  .content-positioner {
    position: absolute;
    padding: 40px 30px;
  }

  &.only-child {
    .content-positioner {
      padding: 80px;

      ${theme.below.lg} {
        padding: 40px 30px;
      }
    }
  }

  .desktop-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &.hide {
      display: none;
    }

    &.top-left {
      background: radial-gradient(
        ellipse at left top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-left {
      background: radial-gradient(
        ellipse at left center,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-left {
      background: radial-gradient(
        ellipse at left bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }
    &.top-center {
      background: radial-gradient(
        ellipse at center top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-center {
      background: radial-gradient(
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-center {
      background: radial-gradient(
        ellipse at center bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.top-right {
      background: radial-gradient(
        ellipse at right top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-right {
      background: radial-gradient(
        ellipse at right center,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-right {
      background: radial-gradient(
        ellipse at right bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    ${theme.below.lg} {
      &.top-left,
      &.top-center,
      &.top-right {
        background: linear-gradient(
          to bottom,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }

      &.middle-left,
      &.middle-center,
      &.middle-right {
        background: linear-gradient(
          to bottom,
          transparent,
          var(--gradientColor),
          var(--gradientColor),
          transparent
        );
      }

      &.bottom-left,
      &.bottom-center,
      &.bottom-right {
        background: linear-gradient(
          to top,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }
    }

    &.not-only-child {
      &.top-left,
      &.top-center,
      &.top-right {
        background: linear-gradient(
          to bottom,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }

      &.middle-left,
      &.middle-center,
      &.middle-right {
        background: linear-gradient(
          to bottom,
          transparent,
          var(--gradientColor),
          var(--gradientColor),
          transparent
        );
      }

      &.bottom-left,
      &.bottom-center,
      &.bottom-right {
        background: linear-gradient(
          to top,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }
    }
  }

  .html-banner-row-item-content {
    max-width: 600px;
    h1,
    h2,
    h3 {
      font-family: ${theme.fonts.primary};
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 12px;
    }
    p {
      line-height: normal;
    }

    &.light {
      color: ${theme.colors.white};
    }
    &.dark {
      color: ${theme.colors.black};
    }

    /* &.only-child {
      h1,
      h2,
      h3 {
        font-size: 60px;
      }
    } */
  }

  .fake-cta {
    margin-top: 20px;

    &.left {
      margin-left: 0;
      margin-right: auto;
    }
    &.center {
      margin-left: auto;
      margin-right: auto;
    }
    &.right {
      margin-right: 0;
      margin-left: auto;
    }

    &.button-separate {
      position: absolute;
      top: auto;
      bottom: 2rem;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  }
`;

const ContentUnderImageWrapper = styled('div')`
  padding: 40px 0px;

  ${theme.below.lg} {
    padding: 40px 30px;
  }

  .html-banner-row-item-content {
    text-align: left;
    max-width: 100%;

    &.left {
      text-align: left;
    }
    &.center {
      text-align: center;
    }
    &.right {
      text-align: right;
    }
  }
`;

/* Each image keeps its own aspect; CSS picks which is visible (no Above swap) */
const BannerMedia = styled('div')`
  .html-banner-media--desktop {
    display: block;
  }
  .html-banner-media--mobile {
    display: none;
  }

  ${theme.below.lg} {
    .html-banner-media--desktop {
      display: none;
    }
    .html-banner-media--mobile {
      display: block;
    }
  }
`;

const normalizeAspect = (aspectProp, fallback) => {
  const raw = aspectProp?.value ?? aspectProp;
  if (!raw || typeof raw !== 'string') return fallback;
  if (!/^\d+\s*[:/x]\s*\d+$/i.test(raw)) return fallback;
  return raw.replace(/[x/]/gi, ':');
};

export const HtmlBannerRowItem = ({
  desktopImage,
  desktopImageAlt,
  desktopImageAspect,
  mobileImage,
  mobileImageAlt,
  mobileImageAspect,
  contentTheme,
  desktopContent,
  mobileContent,
  desktopContentPosition,
  mobileContentPosition,
  contentBelowImage,
  contentBackdrop,
  buttonSeparate,
  buttonText,
  ghostButton,
  link,
  onlyChild,
  fillFrame = false
}) => {
  const { trackViewPromotion, trackSelectPromotion } = useTracking();

  // Extract title from HTML content for tracking
  const extractTitle = htmlContent => {
    if (!htmlContent || typeof document === 'undefined') return null;
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const heading = tempDiv?.querySelector('h1, h2, h3');
      return heading ? heading?.textContent?.trim() : null;
    } catch (error) {
      // Fallback if DOM parsing fails
      return null;
    }
  };

  // Generate unique banner ID and title
  const bannerTitle = extractTitle(
    desktopContent?.value || mobileContent?.value
  );

  // Create a more unique ID by using available data
  const generateBannerId = () => {
    if (bannerTitle) {
      return bannerTitle?.replace(/\s+/g, '_')?.toLowerCase();
    }

    // Fallback to link URL
    if (link?.value) {
      const urlPath = link?.value?.split('/')?.filter(Boolean)?.join('_');
      return urlPath || 'banner';
    }

    // Fallback to image name
    if (desktopImage?.value) {
      const dImage = desktopImage?.value?.value ?? desktopImage?.value;
      const imageName = dImage?.split('/')?.pop()?.split('.')?.[0];
      return imageName || 'banner';
    }

    return 'banner';
  };

  const bannerId = generateBannerId();
  const displayTitle =
    bannerTitle || link?.value?.split('/')?.pop() || 'Banner';

  // Track promotion view when element is in view
  const handleInView = useCallback(() => {
    if (link?.value) {
      // Only track if it's clickable
      trackViewPromotion({
        itemId: bannerId,
        itemName: displayTitle,
        creativeSlot: 'Front page'
      });
    }
  }, [link?.value, bannerId, displayTitle, trackViewPromotion]);

  const bannerRef = useInViewTracking(handleInView, { threshold: 0.5 });

  const handlePromotionClick = () => {
    if (link?.value) {
      trackSelectPromotion({
        itemId: bannerId,
        itemName: displayTitle,
        creativeSlot: 'Front page'
      });
    }
  };

  const wrapperProps = {};

  if (link?.value) {
    wrapperProps.to = link?.value;
    wrapperProps.ariaLabel = buttonText?.value || 'Link';
    wrapperProps.onClick = handlePromotionClick;
  } else {
    wrapperProps.as = 'div';
  }

  let buttonTheme = 'black';
  switch (contentTheme?.value) {
    case 'light':
      buttonTheme = 'light';
      break;
    case 'dark':
      buttonTheme = 'primary';
      break;
    default:
      console.info(
        'No content theme found for HTML Banner Row Item, defaulting to black'
      );
      break;
  }

  if (ghostButton?.value) {
    buttonTheme += ' ghost';
  }
  const desktopVerticalPosition =
    desktopContentPosition?.value?.split('-')?.[0] ?? 'middle';
  const mobileVerticalPosition =
    mobileContentPosition?.value?.split('-')?.[0] ?? 'middle';
  const desktopHorizontalPosition =
    desktopContentPosition?.value?.split('-')?.[1] ?? 'center';
  const mobileHorizontalPosition =
    mobileContentPosition?.value?.split('-')?.[1] ?? 'center';

  const thirtyPercentBlack = '#0000004d';
  const fiftyPercentBlack = '#00000080';
  const transparent = 'transparent';

  let gradientColor = transparent;

  switch (contentBackdrop?.value) {
    case 'none':
      gradientColor = transparent;
      break;
    case '30%':
      gradientColor = thirtyPercentBlack;
      break;
    case '50%':
      gradientColor = fiftyPercentBlack;
      break;
    default:
      console.info(
        'No content backdrop found for HTML Banner Row Item, defaulting to transparent'
      );
      break;
  }
  wrapperProps.gradientcolor = gradientColor;

  const sizes = [1, 1, 1 / 2, 1 / 2, 1 / 2];
  const sizesOnlyChild = [1, 1, 1, 1, 1];

  const desktopFallback = onlyChild?.value || fillFrame ? '12:5' : '1:1';
  const mobileFallback = onlyChild?.value || fillFrame ? '4:5' : '1:1';
  const desktopAspect = normalizeAspect(desktopImageAspect, desktopFallback);
  const mobileAspect = normalizeAspect(mobileImageAspect, mobileFallback);

  const imageSizes = onlyChild?.value || fillFrame ? sizesOnlyChild : sizes;

  return (
    <HtmlBannerRowItemWrapper
      ref={bannerRef}
      className={cx('html-banner-row-item', onlyChild?.value && 'only-child')}
      {...wrapperProps}
    >
      <BannerMedia>
        <div className="html-banner-media--desktop">
          <Image
            aspect={fillFrame ? undefined : desktopAspect}
            fillAvailableSpace={fillFrame}
            modifiedDate={desktopImage?.modifiedDate}
            sizes={imageSizes}
            cover={true}
            quality={100}
            critical={!!(onlyChild?.value || fillFrame)}
            src={desktopImage?.value}
            alt={desktopImageAlt?.value}
          />
        </div>
        <div className="html-banner-media--mobile">
          <Image
            aspect={fillFrame ? undefined : mobileAspect}
            fillAvailableSpace={fillFrame}
            modifiedDate={mobileImage?.modifiedDate}
            sizes={imageSizes}
            cover={true}
            quality={100}
            src={mobileImage?.value || desktopImage?.value}
            alt={mobileImageAlt?.value || desktopImageAlt?.value}
          />
        </div>
      </BannerMedia>
      <Above breakpoint="lg">
        {matches => (
          <>
            {!contentBelowImage?.value && (
              <>
                <div
                  gradientcolor={gradientColor}
                  className={cx(
                    'desktop-backdrop',
                    matches
                      ? desktopContentPosition?.value ?? 'middle-center'
                      : mobileContentPosition?.value ?? 'middle-center',
                    onlyChild?.value ? 'only-child' : 'not-only-child'
                  )}
                />
                <ContentPositioner
                  verticalPosition={(matches
                    ? desktopVerticalPosition
                    : mobileVerticalPosition
                  )?.toUpperCase()}
                  horizontalPosition={(matches
                    ? desktopHorizontalPosition
                    : mobileHorizontalPosition
                  )?.toUpperCase()}
                >
                  <div>
                    <div
                      className={cx(
                        'html-banner-row-item-content',
                        contentTheme?.value ?? 'dark',
                        onlyChild?.value && 'only-child'
                      )}
                      dangerouslySetInnerHTML={{
                        __html: matches
                          ? desktopContent?.value
                          : mobileContent?.value
                      }}
                    />
                    {buttonText?.value?.length > 0 && (
                      <FakeCTA
                        className={cx(
                          buttonTheme,
                          buttonSeparate?.value && 'button-separate'
                        )}
                      >
                        {buttonText?.value}
                      </FakeCTA>
                    )}
                  </div>
                </ContentPositioner>
              </>
            )}
            {contentBelowImage?.value && (
              <ContentUnderImageWrapper>
                <div
                  className={cx(
                    'html-banner-row-item-content',
                    'dark',
                    matches
                      ? desktopHorizontalPosition
                      : mobileHorizontalPosition
                  )}
                  dangerouslySetInnerHTML={{
                    __html: matches
                      ? desktopContent?.value
                      : mobileContent?.value
                  }}
                />
                {buttonText?.value?.length > 0 && (
                  <FakeCTA
                    className={cx(
                      'primary',
                      matches
                        ? desktopHorizontalPosition
                        : mobileHorizontalPosition
                    )}
                  >
                    {buttonText?.value}
                  </FakeCTA>
                )}
              </ContentUnderImageWrapper>
            )}
          </>
        )}
      </Above>
    </HtmlBannerRowItemWrapper>
  );
};
